/**
 * CoursePicker — multi-course switcher dropdown rendered into the app header.
 *
 * Reads /courses/index.json (mirrored from golf/courses/index.json by sync-courses.sh).
 * Updates the URL `?course=<slug>` and triggers a full reload so the map + brief
 * pick up cleanly. Reload-on-switch keeps the boot path simple and deterministic;
 * we can promote to in-place re-init later when the brief loader supports
 * tear-down + rebuild.
 */
export class CoursePicker {
  /**
   * @param {object} opts
   * @param {HTMLElement} opts.container - element to render the picker into
   * @param {string} opts.activeSlug - currently-loaded course slug
   * @param {string} [opts.indexUrl] - override for /courses/index.json
   */
  constructor({ container, activeSlug, indexUrl = "/courses/index.json" }) {
    this.container = container;
    this.activeSlug = activeSlug;
    this.indexUrl = indexUrl;
    this._select = null;
  }

  async init() {
    let courses = [];
    try {
      const res = await fetch(this.indexUrl, { cache: "no-cache" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const idx = await res.json();
      courses = Array.isArray(idx.courses) ? idx.courses : [];
    } catch (err) {
      console.warn("[course-picker] index fetch failed:", err);
      // Render a single-option fallback for the active course so the UI doesn't go blank
      courses = [{ slug: this.activeSlug, name: this.activeSlug, brief_present: true }];
    }

    // Filter to courses that have a brief (otherwise tap-through is broken)
    const usable = courses.filter((c) => c.brief_present !== false);
    if (!usable.length) {
      this.container.hidden = true;
      return;
    }

    this.container.hidden = false;
    this.container.classList.add("course-picker");

    const select = document.createElement("select");
    select.id = "course-select";
    select.setAttribute("aria-label", "Choose course");

    for (const c of usable) {
      const opt = document.createElement("option");
      opt.value = c.slug;
      opt.textContent = c.name || c.slug;
      if (c.slug === this.activeSlug) opt.selected = true;
      select.appendChild(opt);
    }

    select.addEventListener("change", () => {
      const next = select.value;
      if (!next || next === this.activeSlug) return;
      const nextLabel = select.options[select.selectedIndex]?.text || next;

      // BEACON-50 v0.7 (ER-COMP-3): silent course-switch — add a visible
      // loading state, polite live-region announcement, and disable the
      // select so the user gets feedback that something is happening.
      const briefPanel = document.getElementById("brief-panel");
      const announcer = document.getElementById("brief-announcer");
      const placeholder = briefPanel?.querySelector(".brief-placeholder");
      if (briefPanel) {
        briefPanel.setAttribute("aria-busy", "true");
        // Replace content so the user sees the transition (the reload that
        // follows would otherwise leave the previous course's brief on screen).
        briefPanel.innerHTML = `<p class="brief-placeholder">Loading ${escapeText(nextLabel)}…</p>`;
      }
      if (placeholder) placeholder.textContent = `Loading ${nextLabel}…`;
      if (announcer) announcer.textContent = `Switching to ${nextLabel}…`;
      select.disabled = true;

      const url = new URL(window.location.href);
      url.searchParams.set("course", next);
      // Tiny delay so SR has a beat to announce + the loading frame paints
      // before the navigation throws away this document.
      setTimeout(() => window.location.assign(url.toString()), 80);
    });

    // Pre-pend a small "Course" label and the select
    const label = document.createElement("label");
    label.className = "course-picker-label";
    label.htmlFor = "course-select";
    label.textContent = "⛳";
    label.title = "Switch course";

    this.container.replaceChildren(label, select);
    this._select = select;
  }
}

function escapeText(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[c]));
}
